/**
 * Checks if PostHog is available and optionally if a specific method exists.
 * @param {string} [methodName] - Optional method name to check (e.g., 'getFeatureFlag', 'capture', 'onFeatureFlags')
 * @returns {boolean} Returns true if PostHog is available and method exists (if specified)
 */
export function isPostHogAvailable(methodName) {
  if (typeof window === 'undefined' || !window.posthog || typeof window.posthog !== 'object') {
    return false
  }

  // If method name is provided, check if it exists and is a function
  if (methodName) {
    return typeof window.posthog[methodName] === 'function'
  }

  return true
}

/**
 * Runs a callback when PostHog feature flags are ready.
 * Falls back to a short delay when onFeatureFlags is unavailable.
 * @param {Function} callback - Function to execute once flags are ready
 * @param {object} [options]
 * @param {number} [options.fallbackDelay=500] - Delay (ms) when onFeatureFlags is missing
 * @returns {boolean} Returns true when callback scheduling is attempted
 */
export function onPostHogFeatureFlagsReady(callback, { fallbackDelay = 500 } = {}) {
  if (typeof callback !== 'function') {
    return false
  }

  if (isPostHogAvailable('onFeatureFlags')) {
    window.posthog.onFeatureFlags(() => {
      callback()
    })
    return true
  }

  if (isPostHogAvailable()) {
    const delay = Number.isFinite(fallbackDelay) ? fallbackDelay : 500
    setTimeout(() => {
      callback()
    }, delay)
    return true
  }

  callback()
  return true
}

/**
 * Checks if the current experiment flag matches a given variant.
 * @param {string} flagName - The PostHog flag name
 * @param {string} variant - The variant to check against
 * @returns {boolean}
 */
export function getPostHogExperimentVariant(flagName, variant) {
  if (!flagName || !variant) return false

  if (isPostHogAvailable('getFeatureFlag')) {
    return window.posthog.getFeatureFlag(flagName) === variant
  }

  return false
}

/**
 * Retrieves the value of a PostHog experiment flag.
 * @param {string} flagName - The PostHog flag name
 * @returns {string|null}
 */
export function getPostHogExperimentValue(flagName) {
  if (!flagName) return null

  if (isPostHogAvailable('getFeatureFlag')) {
    return window.posthog.getFeatureFlag(flagName)
  }

  return null
}

/**
 * Captures a PostHog analytics event.
 * Safely handles cases where PostHog is not initialized or unavailable.
 * @param {string} eventName - The name of the event to capture
 * @param {object} properties - Event properties/attributes
 * @returns {boolean} Returns true if event was captured successfully, false otherwise
 */
export function capturePosthogEvent(eventName, properties) {
  // Validate inputs
  if (!eventName || typeof eventName !== 'string') {
    return false
  }
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return false
  }

  // Check if PostHog is available
  if (!isPostHogAvailable('capture')) {
    return false
  }

  // Capture the event with error handling
  try {
    window.posthog.capture(eventName, properties)
    return true
  } catch (error) {
    console.error('capturePosthogEvent: Failed to capture event', error)
    return false
  }
}

/**
 * Checks if a category matches any of the provided categories, case-insensitively.
 * @param {string[]} categories - Allowed categories for the experiment
 * @param {string} category - Category to check
 * @returns {boolean}
 */
export function isPostHogCategoryMatch(categories, category) {
  if (!Array.isArray(categories) || typeof category !== 'string') {
    return false
  }

  const normalizedCategories = categories
    .filter((entry) => typeof entry === 'string')
    .map((entry) => entry.toLowerCase())

  return normalizedCategories.includes(category.toLowerCase())
}
